import { useContext, useEffect, useRef, useState } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Link as RouterLink } from 'react-router-dom';
import { Context } from '../../../AuthContext';
import CalendarPickerDialog from './CalendarPickerDialog';
import AddEventDialog from './AddEventDialog';
import {
  CATEGORY_ORDER,
  CATEGORY_GROUP_LABELS,
  DEFAULT_CATEGORY,
} from './calendarCategories';

const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.calendarlist.readonly';

const Calendar = () => {
  const { user } = useContext(Context);
  const [phase, setPhase] = useState('loading'); // loading | connect | ready | error
  const [calendars, setCalendars] = useState([]);
  // Calendars the user has *added* to their sidebar (chosen in the modal).
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Subset of addedIds currently toggled on — these render in the iframe.
  const [visibleIds, setVisibleIds] = useState(() => new Set());
  // Map of calendarId → category key (see calendarCategories.js).
  const [categories, setCategories] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  // Bumped whenever the user returns to this tab, so the iframe re-fetches
  // and picks up events added on calendar.google.com in a sibling tab.
  const [iframeNonce, setIframeNonce] = useState(0);
  // True once preferences have been loaded from the backend; until then we
  // don't persist local changes (to avoid overwriting saved prefs with the
  // empty defaults during the initial mount).
  const prefsLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const statusRes = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!statusRes.ok) throw new Error(`status_${statusRes.status}`);
        const status = await statusRes.json();
        if (cancelled) return;

        const hasCalendar =
          status.connected && (status.scopes || []).includes(GOOGLE_CALENDAR_SCOPE);
        if (!hasCalendar) {
          setPhase('connect');
          return;
        }

        const calRes = await fetch(
          `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendars`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (calRes.status === 401) {
          if (!cancelled) setPhase('connect');
          return;
        }
        if (!calRes.ok) throw new Error(`calendars_${calRes.status}`);
        const data = await calRes.json();
        if (cancelled) return;

        const list = data.calendars || [];
        setCalendars(list);

        // Load saved prefs and seed the local state. We filter the saved IDs
        // against what's actually still accessible — a calendar the user
        // unsubscribed from in Google should disappear from the sidebar too.
        try {
          const prefsRes = await fetch(
            `${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendar-prefs`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (prefsRes.ok) {
            const prefs = await prefsRes.json();
            const validIds = new Set(list.map((c) => c.id));
            const savedAdded = (prefs.added || []).filter((id) => validIds.has(id));
            const addedSet = new Set(savedAdded);
            const savedVisible = (prefs.visible || []).filter((id) => addedSet.has(id));
            const savedCategories = {};
            Object.entries(prefs.categories || {}).forEach(([id, cat]) => {
              if (addedSet.has(id)) savedCategories[id] = cat;
            });
            if (!cancelled) {
              setAddedIds(addedSet);
              setVisibleIds(new Set(savedVisible));
              setCategories(savedCategories);
            }
          }
        } catch (_) {
          // Non-fatal — just start with empty prefs.
        }

        if (!cancelled) {
          prefsLoadedRef.current = true;
          setPhase('ready');
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err.message || 'Failed to load calendar');
        setPhase('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Persist selection to backend on every change (after the initial load).
  useEffect(() => {
    if (!prefsLoadedRef.current || !user) return;
    let cancelled = false;
    const save = async () => {
      try {
        const token = await user.getIdToken();
        await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}oauth/google/calendar-prefs`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            added: Array.from(addedIds),
            visible: Array.from(visibleIds),
            categories,
          }),
        });
      } catch (_) {
        // Non-fatal — local state still works; next change will retry.
      }
      if (cancelled) return;
    };
    save();
    return () => {
      cancelled = true;
    };
  }, [addedIds, visibleIds, categories, user]);

  // Reload the embed when the user returns to this tab (e.g. after adding
  // an event on Google Calendar in a sibling tab).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setIframeNonce((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  const buildEmbedUrl = () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const params = new URLSearchParams();
    params.append('ctz', tz);
    params.append('mode', 'MONTH');
    params.append('wkst', '1');
    params.append('showTitle', '0');
    params.append('showPrint', '0');
    // Order so the primary calendar is the "main" one
    const ordered = [...calendars.filter((c) => c.primary), ...calendars.filter((c) => !c.primary)];
    ordered
      .filter((cal) => visibleIds.has(cal.id))
      .forEach((cal) => {
        params.append('src', cal.id);
        if (cal.backgroundColor) {
          params.append('color', cal.backgroundColor);
        }
      });
    return `https://calendar.google.com/calendar/embed?${params.toString()}`;
  };

  const containerStyle = {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };

  if (phase === 'loading') {
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={28} />
      </div>
    );
  }

  if (phase === 'connect') {
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 24,
        }}
      >
        <img
          src="/workbench/google.svg"
          alt="Google"
          style={{ width: 48, height: 48, marginBottom: 16 }}
        />
        <h3 style={{ margin: 0, marginBottom: 8 }}>Connect your Google account</h3>
        <p style={{ margin: 0, marginBottom: 16, color: '#475467', maxWidth: 420 }}>
          Link your Google account to view your calendar here.
        </p>
        <RouterLink
          to="/profile/connected-accounts"
          style={{
            background: '#356AC3',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 6,
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          Go to Connected Accounts
        </RouterLink>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
          color: '#B42318',
          padding: 24,
          textAlign: 'center',
        }}
      >
        Could not load your calendar ({errorMessage}). Please try again later.
      </div>
    );
  }

  if (calendars.length === 0) {
    return (
      <div
        style={{
          ...containerStyle,
          alignItems: 'center',
          justifyContent: 'center',
          color: '#475467',
          padding: 24,
          textAlign: 'center',
        }}
      >
        No visible calendars found on your Google account. Enable a calendar in Google Calendar and
        refresh this page.
      </div>
    );
  }

  const orderedAdded = [
    ...calendars.filter((c) => c.primary && addedIds.has(c.id)),
    ...calendars.filter((c) => !c.primary && addedIds.has(c.id)),
  ];

  const calendarsByCategory = CATEGORY_ORDER.reduce((acc, key) => {
    acc[key] = orderedAdded.filter((cal) => (categories[cal.id] || DEFAULT_CATEGORY) === key);
    return acc;
  }, {});

  const toggleVisibility = (id) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = (nextAdded, nextCategories) => {
    setAddedIds(nextAdded);
    // Sync visibility: keep currently-visible calendars that are still added,
    // and auto-show any newly-added ones so the user sees their pick right away.
    setVisibleIds((prev) => {
      const next = new Set();
      nextAdded.forEach((id) => {
        if (!addedIds.has(id) || prev.has(id)) next.add(id);
      });
      return next;
    });
    // Trim categories to only the still-added calendars.
    const trimmed = {};
    nextAdded.forEach((id) => {
      trimmed[id] = nextCategories[id] || DEFAULT_CATEGORY;
    });
    setCategories(trimmed);
    setPickerOpen(false);
  };

  return (
    <div
      style={{
        ...containerStyle,
        flexDirection: 'row',
        gap: 16,
        paddingRight: 8,
        paddingBottom: 8,
      }}
    >
      <aside
        style={{
          flexShrink: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'left',
          marginRight: '24px',
        }}
      >
        <div
          style={{
            color: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            marginBottom: 4,
            fontSize: 14,
          }}
          onClick={() => setPickerOpen(true)}
        >
          <IconButton size="small" sx={{ color: '#1976d2', padding: 0.25 }}>
            <AddCircleIcon fontSize="small" />
          </IconButton>
          Add Calendar
        </div>
        {(() => {
          const addDisabled = orderedAdded.length === 0;
          return (
            <div
              style={{
                color: addDisabled ? '#98A2B3' : '#1976d2',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                cursor: addDisabled ? 'not-allowed' : 'pointer',
                marginBottom: 12,
                fontSize: 14,
              }}
              onClick={() => {
                if (!addDisabled) setEventOpen(true);
              }}
              title={addDisabled ? 'Add a calendar to your sidebar first.' : ''}
            >
              <IconButton
                size="small"
                sx={{ color: addDisabled ? '#98A2B3' : '#1976d2', padding: 0.25 }}
                disabled={addDisabled}
              >
                <AddCircleIcon fontSize="small" />
              </IconButton>
              Add Event
            </div>
          );
        })()}
        {orderedAdded.length === 0 ? (
          <p style={{ margin: 0, color: 'black', fontSize: 12, fontWeight: 700 }}>
            No calendars added yet. Click "Add calendar" to choose which ones to display.
          </p>
        ) : (
          <div>
            {CATEGORY_ORDER.map((key) => {
              const group = calendarsByCategory[key];
              return (
                <section key={key} style={{ marginBottom: 16 }}>
                  <h5
                    style={{
                      margin: '0',
                      fontSize: 14,
                      color: 'black',
                    }}
                  >
                    {CATEGORY_GROUP_LABELS[key]}
                  </h5>
                  {group.length === 0 ? (
                    <i style={{ margin: '0 0 0 2px', color: 'black', fontSize: 13 }}>None</i>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {group.map((cal) => {
                        const checked = visibleIds.has(cal.id);
                        return (
                          <li key={cal.id} style={{ marginBottom: 6 }}>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                fontSize: 13,
                                color: '#344054',
                                lineHeight: 1.3,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleVisibility(cal.id)}
                                style={{
                                  accentColor: cal.backgroundColor || '#356AC3',
                                  width: 14,
                                  height: 14,
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  background: cal.backgroundColor || '#356AC3',
                                  flexShrink: 0,
                                }}
                              />
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={cal.summary}
                              >
                                {cal.summary}
                                {cal.primary && (
                                  <span style={{ color: '#667085', marginLeft: 4 }}>(primary)</span>
                                )}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </aside>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <iframe
          // key forces the iframe to reload when the visible set changes —
          // otherwise the embed sometimes caches the previous src list.
          key={`${iframeNonce}:${Array.from(visibleIds).sort().join('|')}`}
          title="My Google Calendar"
          src={buildEmbedUrl()}
          style={{ border: '1px solid black', width: '100%', flex: 1, minHeight: 0 }}
        />
      </div>
      <CalendarPickerDialog
        open={pickerOpen}
        calendars={calendars}
        initialAddedIds={addedIds}
        initialCategories={categories}
        onClose={() => setPickerOpen(false)}
        onSave={handleSave}
      />
      <AddEventDialog
        open={eventOpen}
        calendars={orderedAdded}
        onClose={() => setEventOpen(false)}
      />
    </div>
  );
};

export default Calendar;
